import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type UserRole = "ADMIN" | "DRIVER" | "SELLER" | string;

export type AuthUser = {
  id: number | string | null;
  username: string;
  first_name: string;
  last_name: string;
  rol: UserRole;
};

type LoginPayload = {
  token: string;
  user: Partial<AuthUser>;
};

type AuthContextValue = {
  token: string | null;
  user: AuthUser | null;
  rol: UserRole | null;
  username: string | null;
  userId: number | string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  displayName: string | null;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => void;
  logout: () => void;
};

const AUTH_TOKEN_KEY = "auth_token";
const AUTH_USER_KEY = "auth_user";

const LEGACY_KEYS = ["user", "username", "rol"];

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function normalizeUser(user?: Partial<AuthUser> | null): AuthUser | null {
  if (!user) {
    return null;
  }

  return {
    id: user.id ?? null,
    username: user.username ?? "",
    first_name: user.first_name ?? "",
    last_name: user.last_name ?? "",
    rol: user.rol ?? "",
  };
}

function clearAuthStorage() {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);

    for (const key of LEGACY_KEYS) {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error("No se pudo limpiar la sesión en localStorage", error);
  }
}

function redirectToLogin() {
  if (window.location.pathname !== "/") {
    window.location.replace("/");
  }
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch (error) {
    console.error("No se pudo leer auth_token desde localStorage", error);
    return null;
  }
}

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<AuthUser>;
    return normalizeUser(parsed);
  } catch (error) {
    console.error("No se pudo leer auth_user desde localStorage", error);
    return null;
  }
}

function buildFullName(user: AuthUser | null): string | null {
  if (!user) {
    return null;
  }

  const fullName = [user.first_name, user.last_name]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ");

  return fullName || null;
}

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(() => readStoredToken());
  const [user, setUser] = useState<AuthUser | null>(() => readStoredUser());

  const login = useCallback((payload: LoginPayload) => {
    const normalizedUser = normalizeUser(payload.user);

    if (!normalizedUser) {
      throw new Error("Login inválido: user ausente");
    }

    localStorage.setItem(AUTH_TOKEN_KEY, payload.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(normalizedUser));

    setToken(payload.token);
    setUser(normalizedUser);
  }, []);

  const logout = useCallback(() => {
    clearAuthStorage();
    setToken(null);
    setUser(null);
    redirectToLogin();
  }, []);

  const fullName = useMemo(() => buildFullName(user), [user]);
  const displayName = fullName || user?.username || null;

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      rol: user?.rol ?? null,
      username: user?.username ?? null,
      userId: user?.id ?? null,
      firstName: user?.first_name ?? null,
      lastName: user?.last_name ?? null,
      fullName,
      displayName,
      isAuthenticated: Boolean(token && user),
      login,
      logout,
    }),
    [token, user, fullName, displayName, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }

  return context;
}